import { useEffect, useState } from 'react';
import { formatPortalDateTime, type TimeDisplayMode } from '../utils/datetime';

type ComplianceEventItem = {
  eventId: string;
  createdAt: string;
  actionType: 'OPT_IN' | 'OPT_OUT';
  sourceType: string;
  status: string;
  keyword: string | null;
  fromNumber: string;
  toNumber: string;
};

type ComplianceEventDetail = {
  Event: ComplianceEventItem & {
    keywordEventId?: string | null;
    triggerMessageId?: string | null;
  };
  Actions: Array<{
    actionId: string;
    actionType: string;
    status: string;
    updatedAt: string;
  }>;
};

export function OptOutsRoute({ timeDisplayMode }: { timeDisplayMode: TimeDisplayMode }) {
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<ComplianceEventItem[]>([]);
  const [selected, setSelected] = useState<ComplianceEventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/compliance-events?pageSize=100', { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }
        const payload = (await response.json()) as { Data?: { Items?: ComplianceEventItem[] } };
        setItems(payload.Data?.Items ?? []);
      } catch (loadError) {
        setItems([]);
        setError(loadError instanceof Error ? loadError.message : String(loadError));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function openDetail(eventId: string): Promise<void> {
    setSelected(null);
    try {
      const response = await fetch(`/api/compliance-events/${encodeURIComponent(eventId)}`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }
      const payload = (await response.json()) as { Data?: ComplianceEventDetail };
      setSelected(payload.Data ?? null);
    } catch (detailError) {
      setError(detailError instanceof Error ? detailError.message : String(detailError));
    }
  }

  return (
    <>
      <div className="page-pretitle">Compliance</div>
      <h2 className="page-title mb-3">Opt-outs</h2>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      <div className="card mb-3">
        <div className="card-header">
          <h3 className="card-title">Recent Compliance Events</h3>
        </div>
        <div className="table-responsive">
          <table className="table card-table table-vcenter">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>From</th>
                <th>To</th>
                <th>Source</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length < 1 ? (
                <tr>
                  <td className="text-secondary" colSpan={6}>
                    No compliance events found.
                  </td>
                </tr>
              ) : null}
              {items.map((item) => (
                <tr key={item.eventId} role="button" onClick={() => void openDetail(item.eventId)}>
                  <td>{formatPortalDateTime(item.createdAt, timeDisplayMode)}</td>
                  <td>{item.actionType}</td>
                  <td>{item.fromNumber}</td>
                  <td>{item.toNumber}</td>
                  <td>{item.sourceType}</td>
                  <td>{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {selected?.Event ? (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title mb-0">Event Detail</h3>
          </div>
          <div className="card-body">
            <div><strong>Event ID:</strong> {selected.Event.eventId}</div>
            <div><strong>Keyword:</strong> {selected.Event.keyword ?? '—'}</div>
            <div><strong>Linked Keyword Event:</strong> {selected.Event.keywordEventId ?? '—'}</div>
            <div><strong>Trigger Message:</strong> {selected.Event.triggerMessageId ?? '—'}</div>
            <hr />
            <h4 className="h5">Actions</h4>
            <ul className="mb-0">
              {(selected.Actions ?? []).map((action) => (
                <li key={action.actionId}>
                  {action.actionType} — {action.status} ({formatPortalDateTime(action.updatedAt, timeDisplayMode)})
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </>
  );
}
